import { useQuery } from '@apollo/client';

import useCompanyContext from 'common/hooks/useCompanyContext';

import { TeamMembersInvitationQuotaDocument } from './teamMembersInvitationQuota.gql';

const useTeamMembersInvitationQuota = (options?: { skip?: boolean }) => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error(
      "'companyProfileId' is undefined in 'useTeamMembersInvitationQuota'",
    );
  }

  const { data, ...hook } = useQuery(TeamMembersInvitationQuotaDocument, {
    skip: options?.skip,
    variables: { companyProfileId },
  });

  const teamMemberInvitationsQuota = data?.viewer.company.subscriptionPlan
    ?.quotas?.teamMemberInvitationsQuota || { limit: 0, used: 0 };

  const { limit: invitationsLimit, used: invitationsUsedCount } =
    teamMemberInvitationsQuota;

  const invitationsLeft = invitationsLimit - invitationsUsedCount;
  const hasInvitationsLeft = invitationsLeft > 0;
  const hasNoMoreInvitationsLeft = !hasInvitationsLeft;
  const canBuyExtraSeats =
    data?.viewer.company.pricingPlan.limits.companyUserAccess.isUnlimited ??
    false;
  const extraSeatCost =
    data?.viewer.company.pricingPlan.fees.extraSeatMonth.fixed ?? 0;

  const higherPlanWithMoreInvitations =
    data?.viewer.company.pricingPlan.plansWithMoreCompanyUserAccess[0];

  return {
    ...hook,
    canBuyExtraSeats,
    extraSeatCost,
    hasInvitationsLeft,
    hasNoMoreInvitationsLeft,
    higherPlanWithMoreInvitations,
    invitationsLeft: Math.max(0, invitationsLeft),
    teamMemberInvitationsQuota,
  };
};

export function refetchTeamMembersInvitationQuotaQuery(
  companyProfileId: string,
) {
  return {
    query: TeamMembersInvitationQuotaDocument,
    variables: { companyProfileId },
  };
}

export default useTeamMembersInvitationQuota;
